import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProfile } from "@/hooks/use-profile";
import { useSubscriptionStatus } from "@/hooks/use-payments";
import { useBankAccounts } from "@/hooks/use-banking";
import {
  hasSeenWelcomeGuide,
  markWelcomeGuideSeen,
  welcomeGuideHostOpens,
} from "@/lib/welcomeGuide";
import { WelcomeGuideDialog } from "./WelcomeGuideDialog";

/**
 * Decides whether to show the welcome guide, once per user (BEN-141 page guidance,
 * slice 2).
 *
 * WHY IT LIVES IN AppLayout AND NOT IN Dashboard. `Dashboard` early-returns
 * `<ConnectBankGate />` when the user has no active accounts — so a user who SKIPPED the
 * bank connect never reaches the dashboard body, and that is precisely the user whose
 * checklist still has something in it. Mounting here also covers a user whose
 * `default_view` sends them to Transactions or Budgets instead (App.tsx:63), which a
 * dashboard-only mount would miss. Same reasoning as TrialBanner and BackToTop, hosted
 * here for the same "every authed route" reason.
 *
 * ONLY AFTER SETUP (X4). Onboarding itself is outside AppLayout, but /payment is not:
 * it sits inside this layout and is exempt from the onboarding gate, and signup ends
 * there. An earlier version of this comment said the box "cannot fire mid-funnel"; it
 * could, and did, for every new signup, opening the after-setup box over the payment
 * page with cards the gate bounced back to /onboarding. A user who has not finished
 * setup now gets the box on setup's Welcome step instead (see `welcomeGuideHostOpens`).
 *
 * BOTH QUERIES MUST RESOLVE BEFORE IT OPENS. Opening on the profile alone would paint the
 * checklist with `bankConnected: false` while the accounts query was still in flight —
 * i.e. tell a user with a connected bank that they had not connected one, then silently
 * correct itself. Both are already fetched by other consumers of this layout, so it is
 * warm cache in practice, not an extra round trip.
 *
 * NO EFFECT, DELIBERATELY. The obvious shape here is `useEffect(() => setOpen(...))`, and
 * it is both lint-flagged (setState in an effect → cascading renders) and more fragile:
 * it needs a second "have I decided yet" flag to stop a background refetch reopening a
 * dismissed dialog. Deriving visibility instead needs neither. Dismissal is the only
 * state, and it is also written to storage, so the dialog cannot come back either way.
 */
export function WelcomeGuideHost() {
  const profileQ = useProfile();
  const accountsQ = useBankAccounts();
  const statusQ = useSubscriptionStatus();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const userId = profileQ.data ? String(profileQ.data.id) : null;

  // Read storage once per user rather than on every render of the layout. Reading it in
  // render is the existing convention here — `useSidebarCollapsed` above and
  // `readRememberedStep()` in the onboarding resolver both do the same.
  const alreadySeen = useMemo(() => (userId ? hasSeenWelcomeGuide(userId) : true), [userId]);

  // Paywall rehearsal (2026-10-15): never over the plan page, and not while the paywall
  // refuses the pages the box points to. The status must have loaded to say so.
  const status = statusQ.data;
  const paywalled =
    status !== undefined && status.gating_enabled && !status.is_entitled && !status.is_exempt;
  const onPlanPage = pathname === "/payment" || pathname.startsWith("/payment/");
  const ready =
    !profileQ.isPending && !accountsQ.isPending && !statusQ.isPending && userId !== null;
  const opens =
    !onPlanPage &&
    welcomeGuideHostOpens({
      ready,
      dismissed,
      alreadySeen,
      onboardingComplete: profileQ.data?.profile.onboarding_complete,
      paywalled,
    });
  if (!opens || !userId) return null;

  return (
    <WelcomeGuideDialog
      open
      onDismiss={() => {
        markWelcomeGuideSeen(userId);
        setDismissed(true);
      }}
      bankConnected={(accountsQ.data?.length ?? 0) > 0}
      // `onboarding_complete` is written by ForecastGeneratingStep at the end of the
      // funnel, so it IS "the first forecast has been built" — see WelcomeGuideDialog.
      firstForecastBuilt={profileQ.data?.profile.onboarding_complete ?? false}
    />
  );
}
